import { useEffect, useRef, useState } from 'react';
import { CheckIcon, RotateCcwIcon } from 'lucide-react';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from '@/components/ui/field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '../../lib/apiClient';
import {
  useBewaarHulpvraagPrompt,
  useBewaarSamenvattingPrompt,
  useHulpvraagPrompt,
  useSamenvattingPrompt,
  useTaalmodel,
  useZetTaalmodel,
  type PromptInstelling,
} from '../beheer/beheer.api';

/**
 * Waar de uitkomst mee wordt bijgestuurd: welk model, en met welke opdracht.
 * Die twee horen bij elkaar, want een prompt die op het ene model goed werkt
 * hoeft dat op het andere niet te doen.
 */
export function VerwerkingPaneel() {
  return (
    <>
      <Taalmodel />
      <PromptVeld
        bewaar={useBewaarSamenvattingPrompt()}
        data={useSamenvattingPrompt().data}
        plaatshouder="{{VAKTERMEN}}"
        titel="Samenvatten"
      />
      <PromptVeld
        bewaar={useBewaarHulpvraagPrompt()}
        data={useHulpvraagPrompt().data}
        plaatshouder="{{ONDERWERPEN}}"
        titel="Hulpvragen zoeken"
      />
    </>
  );
}

/**
 * Welk taalmodel het werk doet. Eén keuze voor samenvatten én hulpvragen
 * zoeken: twee knoppen suggereren dat je die los kunt afstemmen, terwijl het in
 * de praktijk één beslissing is over wat een gesprek mag kosten.
 *
 * Een `RadioGroup` en geen rij knoppen. Het is één keuze uit een lijst, elk met
 * een omschrijving eronder, en dat is precies wat `FieldLabel` om een `Field`
 * heen oplevert: een aanklikbaar vlak dat oplicht als het gekozen is. Kiezen
 * bewaart meteen; er valt hier niets af te wegen tot je op opslaan drukt.
 */
function Taalmodel() {
  const { data } = useTaalmodel();
  const zet = useZetTaalmodel();
  const [fout, setFout] = useState('');

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Taalmodel</CardTitle>
          <CardDescription>Voor samenvatten en hulpvragen zoeken</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            disabled={zet.isPending}
            onValueChange={async (id) => {
              setFout('');
              try {
                await zet.mutateAsync(id);
              } catch (err) {
                setFout(err instanceof ApiError ? err.message : String(err));
              }
            }}
            value={data?.model ?? ''}
          >
            {data?.keuzes.map((m) => (
              <FieldLabel htmlFor={`model-${m.id}`} key={m.id}>
                <Field orientation="horizontal">
                  <FieldContent>
                    <FieldTitle>
                      {m.naam}
                      {m.id === data.standaard && (
                        <Badge className="ml-2" variant="secondary">
                          standaard
                        </Badge>
                      )}
                    </FieldTitle>
                    <FieldDescription>{m.omschrijving}</FieldDescription>
                  </FieldContent>
                  <RadioGroupItem id={`model-${m.id}`} value={m.id} />
                </Field>
              </FieldLabel>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}
    </div>
  );
}

/**
 * Een prompt bijstellen. Twee keer dezelfde vorm, samenvatten en hulpvragen
 * zoeken, dus één component met een andere plaatshouder.
 */
function PromptVeld({
  titel,
  plaatshouder,
  data,
  bewaar,
}: {
  titel: string;
  plaatshouder: string;
  data: PromptInstelling | undefined;
  bewaar: ReturnType<typeof useBewaarSamenvattingPrompt>;
}) {
  const [prompt, setPrompt] = useState('');
  const [melding, setMelding] = useState('');
  const [fout, setFout] = useState('');
  const geladen = useRef<string | null>(null);

  useEffect(() => {
    if (data && geladen.current !== data.prompt) {
      geladen.current = data.prompt;
      setPrompt(data.prompt);
    }
  }, [data]);

  const gewijzigd = Boolean(data) && prompt !== data?.prompt;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{titel}</CardTitle>
          <CardDescription>{plaatshouder} wordt ingevuld</CardDescription>
          <CardAction>
            <Badge variant={data?.isStandaard ? 'outline' : 'secondary'}>
              {data?.isStandaard ? 'standaardprompt' : 'aangepast'}
            </Badge>
          </CardAction>
        </CardHeader>
        <CardContent>
          {/* Vaste letterbreedte: in een prompt doen regeleindes en uitlijning
              mee, en `{{VAKTERMEN}}` moet je als één ding kunnen zien. */}
          <Textarea
            aria-label={`Prompt voor ${titel.toLowerCase()}`}
            className="min-h-64 font-mono text-xs"
            onChange={(e) => setPrompt(e.target.value)}
            spellCheck={false}
            value={prompt}
          />
        </CardContent>
        <CardFooter className="justify-end gap-2">
          {data && !data.isStandaard && (
            <Button onClick={() => setPrompt(data.standaard)} variant="ghost">
              <RotateCcwIcon data-icon="inline-start" />
              Standaard terugzetten
            </Button>
          )}
          <Button
            disabled={!gewijzigd || bewaar.isPending}
            onClick={async () => {
              setFout('');
              setMelding('');
              try {
                await bewaar.mutateAsync(prompt);
                geladen.current = prompt;
                setMelding('Opgeslagen.');
              } catch (err) {
                setFout(err instanceof ApiError ? err.message : String(err));
              }
            }}
          >
            {bewaar.isPending && <Spinner data-icon="inline-start" />}
            {bewaar.isPending ? 'Bezig…' : 'Opslaan'}
          </Button>
        </CardFooter>
      </Card>

      {melding && !gewijzigd && (
        <Alert>
          <CheckIcon />
          <AlertTitle>{melding}</AlertTitle>
        </Alert>
      )}
      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}
    </div>
  );
}
